import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';

import * as yup from 'yup';
import { i18n as I18n } from '../../../utils/i18n';

// Constants
import { yupServiceSchema } from '../../../constants/yupServiceSchema';

// Components
import Service from '../service';
import Checkbox from '../../fields/checkbox';
import ServiceDetailsContainer from '../serviceDetailsContainer';

// Hooks
import useValidation from '../../../hooks/useValidation';
import { useApiRequest } from '../../../hooks/useApiRequest';

// eslint-disable-next-line func-names
yup.addMethod(yup.object, 'uniqueProperty', function (propertyName, message) {
  // eslint-disable-next-line func-names
  return this.test('unique', message, function (value) {
    if (!value || !value[propertyName]) {
      return true;
    }

    const { path } = this;
    const options = [...this.parent];
    const currentIndex = options.indexOf(value);
    const subOptions = options.slice(0, currentIndex);

    if (
      subOptions.some(option => option[propertyName] === value[propertyName])
    ) {
      throw this.createError({
        path: `${path}.${propertyName}`,
        message,
      });
    }

    return true;
  });
});

const servicesSchema = yup
  .array()
  .of(
    yupServiceSchema.uniqueProperty(
      'title',
      I18n.t('registration.service_details.services_not_unique')
    )
  );

const NewServiceDetails = ({
  fields,
  submissionErrors,
  handleFieldChange,
  handleResetField,
  handleSubmitCallback,
  handleGoBack,
  termsAndConditionsEnabled = true,
  previousStep,
}) => {
  const schema = useMemo(() => {
    const additionalSchemas = {};

    if (termsAndConditionsEnabled) {
      additionalSchemas.agreePrivacy = yup.string().required(
        I18n.t('validations.required', {
          name: I18n.t(
            'registration.service_details.fields.agree_privacy.label'
          ),
        })
      );

      additionalSchemas.agreeTerms = yup.string().required(
        I18n.t('validations.required', {
          name: I18n.t('registration.service_details.fields.agree_terms.label'),
        })
      );
    }

    return yup.object().shape({
      services: servicesSchema,
      ...additionalSchemas,
    });
  }, [termsAndConditionsEnabled]);

  const { errors, validate, isValid } = useValidation(fields, schema);

  const [availableServices, setAvailableServices] = useState([]);
  const [commissioningBodies, setCommissioningBodies] = useState([]);

  const apiRequest = useApiRequest();

  useEffect(() => {
    const fetchAvailableServices = async () => {
      const { data } = await apiRequest.get('/v1/organisations/services');
      setAvailableServices(data);
    };

    const fetchCommissioningBodies = async () => {
      const { data } = await apiRequest.get(`/v1/organisations/ccg_regions`);
      setCommissioningBodies(
        data.map(option => ({
          label: option.name,
          value: option.id,
        }))
      );
    };

    fetchAvailableServices();
    fetchCommissioningBodies();
  }, [apiRequest]);

  // When services change, run validation to check uniqueness of service combinations.
  const hasMounted = useRef(false);
  useEffect(() => {
    if (hasMounted.current) {
      validate();
    } else {
      hasMounted.current = true;
    }
  }, [fields.services, validate]);

  const newService = () => {
    const template = {};
    Object.keys(fields.services[0]).forEach(key => {
      template[key] = '';
    });

    return template;
  };

  const nextServicePosition = () => {
    const currentMax = fields.services.reduce(
      (max, service) => Math.max(max, service.position),
      0
    );
    return currentMax + 1;
  };

  const addService = () => {
    const service = newService();
    service.position = nextServicePosition();
    service.isOpen = true;

    const currentServices = fields.services.map(item => ({
      ...item,
      isOpen: false,
    }));
    handleResetField('services', [...currentServices, { ...service }]);
  };

  const removeService = position => {
    const services = fields.services.filter(item => item.position !== position);
    handleResetField('services', services);
  };

  const updateService = useCallback(
    service =>
      handleResetField('services', services =>
        services.map(item =>
          item.position === service.position ? service : item
        )
      ),
    [handleResetField]
  );

  const toggleServiceVisibility = position => {
    const services = fields.services.map(item =>
      item.position === position ? { ...item, isOpen: !item.isOpen } : item
    );
    handleResetField('services', services);
  };

  const handleChange = event => {
    handleFieldChange(event);
  };

  const handleSubmit = event => {
    event.preventDefault();

    validate();
    if (isValid()) {
      handleSubmitCallback();
    }

    return false;
  };

  const agreementLabelContent = (id, text, href) => (
    <>
      {I18n.t('registration.service_details.i_have_read_and_agree_to')}
      &nbsp;
      <Link href={href}>
        <a target="_blank">{text}</a>
      </Link>
    </>
  );

  return (
    <ServiceDetailsContainer
      handleSubmit={handleSubmit}
      submissionErrors={submissionErrors}
      isValid={isValid()}
      handleGoBack={handleGoBack}
      previousStep={previousStep}
    >
      <ul className="services-list" data-cy="newServicesList">
        {fields.services.map((serviceFields, index) => (
          <Service
            number={index + 1}
            key={serviceFields.position}
            isOpen={serviceFields.isOpen}
            serviceFields={serviceFields}
            availableServices={availableServices}
            commissioningBodies={commissioningBodies}
            handleUpdateService={updateService}
            handleRemoveService={
              index === 0 && fields.services.length === 1
                ? null
                : () => removeService(serviceFields.position)
            }
            handleToggleServiceVisibility={() =>
              toggleServiceVisibility(serviceFields.position)
            }
            notUnique={!!errors[`services[${index}].title`]}
          />
        ))}
      </ul>
      <p>
        <button
          onClick={addService}
          className="button button--medium button--primary"
          disabled={!servicesSchema.isValidSync(fields.services)}
          data-testid="addService"
          data-cy="addService"
        >
          {I18n.t('registration.service_details.add_another_service')}
        </button>
      </p>
      {termsAndConditionsEnabled && (
        <div className="acceptance-container">
          <Checkbox
            label={agreementLabelContent(
              'agreePrivacy',
              I18n.t('registration.service_details.fields.agree_privacy.label'),
              '/privacy-policy'
            )}
            name="agreePrivacy"
            id="agreePrivacy"
            checked={fields.agreePrivacy === '1'}
            value="1"
            onChange={handleChange}
          />
          <Checkbox
            label={agreementLabelContent(
              'agreeTerms',
              I18n.t('registration.service_details.fields.agree_terms.label'),
              '/terms-conditions'
            )}
            name="agreeTerms"
            id="agreeTerms"
            checked={fields.agreeTerms === '1'}
            value="1"
            onChange={handleChange}
          />
        </div>
      )}
    </ServiceDetailsContainer>
  );
};

NewServiceDetails.propTypes = {
  fields: PropTypes.object.isRequired,
  submissionErrors: PropTypes.arrayOf(PropTypes.string),
  handleFieldChange: PropTypes.func.isRequired,
  handleResetField: PropTypes.func.isRequired,
  handleSubmitCallback: PropTypes.func.isRequired,
  handleGoBack: PropTypes.func,
  previousStep: PropTypes.number,
};

NewServiceDetails.defaultProps = {
  previousStep: 2,
};

export default NewServiceDetails;
